import styled, { createGlobalStyle } from 'styled-components'
import {
  Modal,
  Button,
  TextField,
  Snackbar,
  InputAdornment, MenuItem
} from '@material-ui/core'
import { Link } from 'react-router-dom'
import { color as d3Color } from 'd3-color'
import { ArrowDropUp } from '@material-ui/icons'

const BASE_PX = 16

// 1 px = 0.0625 rem
export const px2rem = (input = 0) => parseFloat(input / BASE_PX).toFixed(4)

// usage background-color: ${darken(colors.lightGrey, 0.2)};
export const darken = (color, percent) => {
  return d3Color(color).darker(percent || 1)
}

// usage background-color: ${lighten(colors.darkGrey, 0.2)};
export const lighten = (color, percent) => {
  return d3Color(color).brighter(percent || 1)
}

export const maxAppWidth = '1140px'

export const svgIconStyle = (directives) => ({
  display: 'inline-block',
  ...directives
})

/** **********************************************************
 * Colors
 ********************************************************** */
export const colors = {
  govDocsRed: '#d12229',
  lightRed: '#D73E3E',
  govDocsBlue: '#3e6c90',
  mediumBlue: '#2d7abf',
  lightBlue: '#b0d3ea',
  green: '#008037',
  lightGreen: '#90CAA9',
  yellow: '#f7ea91',
  darkGrey: '#393a3a',
  mediumGrey: '#9d9fa1',
  lightGrey: '#e8e8e8',
  backgroundGrey: '#f5f5f5',
  white: '#ffffff'
}

export const theme = {
  colors: {
    blueBackground: colors.govDocsBlue,
    background: colors.backgroundGrey,
    divider: colors.lightGrey,
    text: colors.darkGrey,
    redText: colors.govDocsRed,
    blueText: colors.mediumBlue,
    darkBlueText: colors.govDocsBlue,
    whiteText: colors.white
  }
}

/** **********************************************************
 * Typography
 ********************************************************** */
export const LoginTitle = styled.h2`
  height: 24px;
  color: ${theme.colors.text};
  font-family: 'Open Sans';
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-align: left;
`

export const StyledH1 = styled.h1`
  text-transform: ${(props) => (props.uppercase ? 'uppercase' : 'capitalize')};
  font-size: 50px;
  font-weight: 600;
  text-align: center;
  color: ${theme.colors.text};
  padding: 1rem 0;
  margin: 0 auto;
`

export const StyledH2 = styled.h2`
  text-transform: ${(props) => (props.uppercase ? 'uppercase' : 'capitalize')};
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  color: ${theme.colors.text};
  padding: 1rem 0;
  margin: 0 auto;
`

export const StyledH3 = styled.h3`
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  color: ${theme.colors.redText};
  padding: 1rem 0;
  margin: 0 auto;
`

export const H3GovDocsRed = styled.h3`
  font-size: 20px;
  font-weight: 600;
  text-align: left;
  color: ${theme.colors.redText};
  padding: 1rem 0;
  margin: 0 auto;
`

export const StyledH4 = styled.h4`
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  color: ${theme.colors.darkBlueText};
  padding: 1rem 0;
  margin: 0 auto;
`

export const StyledH5 = styled.h5`
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: ${theme.colors.text};
  padding: 1rem 0;
  margin: 0 auto;
`

export const StyledParagraph = styled.p`
  font-size: 15px;
  font-weight: 400;
`

export const StyledDiv = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: ${theme.colors.text};
`

export const StyledSpan = styled.span`
  color: ${theme.colors.text};
`

export const StyledHeaderSpan = styled(StyledSpan)`
  font-size: 24px;
  font-weight: 600;
  margin-left: 100px;
`

export const StyledParagraph600FontWeight0Margin = styled.p`
  font-size: 15px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 0;
`

export const StyledAddressDiv = styled(StyledDiv)`
  margin: 0;
  &&& span {
    display: block;
  }
`

export const BorderlessParagraphWithBlockSpans = styled(StyledParagraph)`
  margin: 0;
  &&& span {
    display: block;
  }
`

export const InlineAddress = styled.span`
  font-style: normal;
  span: {
    display: block;
  }
`

export const StyledParagraphBlock = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: ${theme.colors.text};
  display: inline-block;
  margin: 1em 0px;
`

/** **********************************************************
 * Links
 ********************************************************** */

export const StyledExternalLink = styled.a`
  color: ${theme.colors.blueText};
  text-decoration: none;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  & {
    :focus {
      outline: none;
    }
  }
`

// Used for internal links using react-router
export const StyledLink = styled(Link)`
  color: ${theme.colors.blueText};
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  && {
    text-decoration: none;
  }
`

/** ***********************************************************
 * Form elements
 *********************************************************** */
export const StyledButton = styled(Button)`
  && {
    background-color: ${colors.govDocsRed};
    color: ${theme.colors.whiteText};
    border-radius: 3px;
    height: 45px;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    /* padding: 5px; */
    text-transform: none;

    :hover {
      background-color: ${colors.lightRed};
    }
  }

  &:disabled {
    background-color: ${colors.lightGrey};
  }
`

export const StyledRightButton = styled(StyledButton)`
  float: right;
  margin-right: 10px;
`

export const TextButton = styled(Button)`
  variant: ${(props) => props.variant || 'text'};
  && {
    color: ${theme.colors.blueText};
    background-color: inherit;
    text-transform: inherit;
    font-size: inherit;
    font-weight: inherit;
    display: inline;
    height: inherit;
    min-height: unset;
    padding: 0;
    :hover {
      background-color: inherit;
    }
  }
`

export const StyledTextField = styled(TextField)`
  label {
    color: ${theme.colors.text};
    position: unset;
    transform: unset;
    text-align: initial;
    height: 12px;
    font-family: "Open Sans";
    font-size: 15px;
    font-weight: 600;
    line-height: 30px;
  }
  input {
    background-color: ${colors.white};
    color: ${theme.colors.text};
    height: 43px;
    padding-left: 13px;
    border: 1px solid;
  }
`
export const StyledPosterCheckTextField = styled(TextField)`
  label {
    color: ${theme.colors.text};
    position: unset;
    transform: unset;
    text-align: initial;
    height: 12px;
    font-family: "Open Sans";
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    padding-bottom: 20px;
  }
  input {
    background-color: ${colors.white};
    color: ${theme.colors.text};
    height: 43px;
    font-size: 16px;
    padding-left: 13px;
    border: 1px solid;
  }
`

export const StyledSearchField = styled(TextField)`
  background-color: ${colors.lightGrey};
  width: 100%;

  input {
    background-color: ${colors.lightGrey};
    color: ${theme.colors.text};
    font-family: 'Open Sans';
    font-size: 15px;
    font-style: italic;
    font-weight: 600;
    line-height: 22px;
    height: 36px;
    padding-left: 13px;
    max-width: 100%;
  }

  input::placeholder {
    color: ${theme.colors.text};
    font-family: 'Open Sans';
    font-size: 15px;
    font-style: italic;
    font-weight: 600;
    line-height: 22px;
  }
`

/** ***********************************************************
 * Pagination components
 *********************************************************** */
export const PaginationWrapper = styled.div`
  float: left;
  margin-top: 40px;
  width: 100%;

  & > .pagination {
    float: right;
    margin: 0;
    padding: 0;
  }

  & > .pagination li {
    display: inline;
    list-style: none;
    margin: 5px;
    cursor: pointer;
    color: ${theme.colors.text};
    font-family: 'Open Sans';
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
  }

  & > .pagination li:focus {
    outline: none;
  }

  & > .pagination li a:focus {
    outline: none;
  }

  & > .pagination li.selected {
    border-bottom: 3px solid ${theme.colors.redText};
    color: ${theme.colors.redText};
  }
`

export const PaginationArrow = styled(ArrowDropUp)`
  height: 16px;
  ${(props) => (props.direction === 'right'
    ? 'transform: translateY(4px) rotate(-90deg);'
    : 'transform: translateY(4px) rotate(90deg);')}
  position: relative;
  top: 2px;
  width: 16px;
  color: ${theme.colors.text};
`

export const FooterText = styled.div`
  color: ${theme.colors.text};
  float: left;
  font-family: 'Open Sans';
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
`

/** ***********************************************************
 * Custom components
 *********************************************************** */
export const StyledDialog = styled(Modal)`
  && {
   ${(props) => (props.customBackgroundColor ? props.customBackgroundColor : null)}
  }
`

export const DialogContainer = styled.div`
  position: absolute;
  border-radius: 4px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
  outline:none!important;
  width: 60%;
`

export const StyledDialogContent = styled.div`
  overflow-y: auto;
  max-height: 70vh;
  min-height: 30vh;
  padding: 16px 24px 16px 24px;
  text-align: center;
  flex-direction: row;
  background-color: ${colors.white};
`

export const StyledDialogTitle = styled.div`
  color: white;
  font-size: 18px;
  height: 40px;
  padding: 16px 24px;
  background-color: ${colors.govDocsBlue};
  && h6 {
    color: ${theme.colors.whiteText};
  }
`

export const StyledSnackbar = styled(Snackbar)`
  > div {
    background-color: ${(props) => (props.type === 'success' ? 'green' : props.type === 'error' ? 'red' : 'blue')};
    border-radius: 0;
    color: ${colors.white};
  }
`

export const StyledMenuItem = styled(MenuItem)`
    color: ${theme.colors.text};
    :hover {
      background-color: ${colors.lightGrey};
    }
`

export const StyledMenuHeader = styled(MenuItem)`
  color: ${theme.colors.text};
  font-weight: bold;
  opacity: 100%;
`

/** ***********************************************************
 * Misc
 *********************************************************** */
export const GraphHeading = styled(StyledH2)``

export const TileStyle = {
  padding: '1rem',
  minHeight: '100%',
  boxShadow: 'unset'
}

export const GridWrapper = styled.div`
  margin: 2rem auto;
  width: 100%;
  max-width: ${maxAppWidth};
  flex: 1 1 auto;
  flex-direction: row;
  align-self: stretch;
  flex-grow: 1;
`
/** ***********************************************************
 * Global
 *********************************************************** */
export const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${theme.colors.background} ;
    margin: 0;
    padding: 0;
    font-family: "Open Sans";
    font-size: 100%;
  }
  li {
    color: ${theme.colors.text};
  }
  ul {
    color: ${theme.colors.text};
  }
  p {
    color: ${theme.colors.text};
  }
  h3 {
    color: ${theme.colors.text};
  }
  #modalTitle {
    color: ${theme.colors.whiteText};
  }
  .link {
    color: ${theme.colors.blueText};
    text-decoration: none;
  }
  .text {
    color: ${theme.colors.text};
  }
  #app {
    height: 100vh;
  }
  #app > div {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  #app > div > * {
    padding: 0 20px;
  }
  header {
    background-color: ${colors.govDocsBlue} ;
    min-height: 90px;
    flex: 1 1 auto;
    flex-direction: row;
    flex-grow: 0;
  }
  header > * {
    max-width: ${maxAppWidth};
    margin: 0 auto;
    width: 100%;
  }
  footer {
    flex: 1 1 auto;
    flex-grow: 0;
    flex-direction: row;
    background-color: ${colors.darkGrey};
    align-content: center;
  }

  .mapTooltip {
    background-color: ${colors.white};
    border: 1px solid ${colors.darkGrey};
    padding : 1rem;
  }

  .svgTransformCenter {
    transform: translate(50%,50%);
  }

  div[role="columnheader"]:focus{outline:unset}


  /* SVG Gradients */
  .filled {
    fill: url(#mainGradient);
  }

  .activeContent:before {
    padding-left: 10px;
    height: 1.5rem;
    margin-left: -13px;
    border-left: 4px solid ${colors.govDocsBlue};
    content: " ";
    width: 1rem;
    z-index: 1000;
  }

  .wrap {
    overflow-wrap: break-word;
    word-break: break-all;
  }
`

export const StyledInputAdornment = styled(InputAdornment)`
  margin: auto 10px;
`
export const CustomH5 = styled(StyledH5)`
  text-align: left;
`
